// frontend/src/components/ui/PdfViewerModal.tsx
import React, { useState } from 'react';
import { Download, Share2, X, FileText, Check, ExternalLink } from 'lucide-react';
import { SavedPdfReport } from '../../utils/pdfStorage';
import { hapticFeedback } from '../../utils/nativeBridge';
import { useLanguage } from '../../contexts/LanguageContext';

interface PdfViewerModalProps {
    isOpen: boolean;
    onClose: () => void;
    report: SavedPdfReport | null;
}

export const PdfViewerModal: React.FC<PdfViewerModalProps> = ({
    isOpen,
    onClose,
    report
}) => {
    const [copied, setCopied] = useState(false);
    const { lang } = useLanguage();
    const isTurkish = lang === 'tr';

    if (!isOpen || !report) return null;

    const handleDownload = () => {
        hapticFeedback('medium');
        const a = document.createElement('a');
        a.href = report.url;
        a.download = report.name || 'lashing-report.pdf';
        document.body.appendChild(a);
        a.click();
        setTimeout(() => document.body.removeChild(a), 100);
    };

    const handleShare = async () => {
        hapticFeedback('light');
        if (navigator.share && report.url.startsWith('http')) {
            try {
                await navigator.share({
                    title: report.name || 'Lashing Calculation Report',
                    text: `Lashing Plan Report - ${report.cargoSummary || ''}`,
                    url: report.url
                });
                return;
            } catch (err) {
                console.log('Share error or cancelled:', err);
            }
        }

        // Fallback: Copy link/notice to clipboard
        try {
            await navigator.clipboard.writeText(report.url);
            setCopied(true);
            setTimeout(() => setCopied(false), 2500);
        } catch (e) {
            console.error('Clipboard copy failed:', e);
        }
    };

    return (
        <div className="fixed inset-0 z-[130] bg-slate-950/85 backdrop-blur-md flex flex-col items-center justify-between p-2 sm:p-4 md:p-6 animate-in fade-in duration-200">
            {/* Top Toolbar Header */}
            <div className="w-full max-w-5xl bg-white/10 dark:bg-[#0D111C]/90 backdrop-blur-xl rounded-2xl p-3 border border-white/15 flex flex-wrap items-center justify-between gap-3 shadow-xl">
                <div className="flex items-center gap-2.5 min-w-0">
                    <div className="w-9 h-9 rounded-xl bg-orange-500/20 border border-orange-500/30 flex items-center justify-center shrink-0">
                        <FileText className="w-5 h-5 text-orange-400" />
                    </div>
                    <div className="min-w-0">
                        <h3 className="text-sm font-bold text-white truncate max-w-[200px] sm:max-w-xs md:max-w-md">
                            {report.name || 'Lashing Plan Report'}
                        </h3>
                        <p className="text-[11px] text-slate-300 truncate">
                            {report.date} {report.cargoSummary ? `• ${report.cargoSummary}` : ''}
                        </p>
                    </div>
                </div>

                <div className="flex items-center gap-2">
                    {/* Share / Copy link button */}
                    <button
                        onClick={handleShare}
                        className="px-3 py-2 bg-white/10 hover:bg-white/20 active:scale-95 text-white text-xs font-semibold rounded-xl transition-all flex items-center gap-1.5 border border-white/10"
                        title="Share Report"
                    >
                        {copied ? (
                            <>
                                <Check className="w-4 h-4 text-emerald-400" />
                                <span className="text-emerald-400 hidden sm:inline">{isTurkish ? 'Kopyalandı!' : 'Copied!'}</span>
                            </>
                        ) : (
                            <>
                                <Share2 className="w-4 h-4 text-orange-400" />
                                <span className="hidden sm:inline">{isTurkish ? 'Paylaş' : 'Share'}</span>
                            </>
                        )}
                    </button>

                    {/* Download Button */}
                    <button
                        onClick={handleDownload}
                        className="px-3.5 py-2 bg-gradient-to-r from-orange-500 to-amber-500 hover:from-orange-600 hover:to-amber-600 active:scale-95 text-white text-xs font-bold rounded-xl shadow-lg shadow-orange-500/25 transition-all flex items-center gap-1.5"
                    >
                        <Download className="w-4 h-4" />
                        <span>{isTurkish ? 'İndir' : 'Download'}</span>
                    </button>

                    {/* Close Button */}
                    <button
                        onClick={() => { hapticFeedback('light'); onClose(); }}
                        className="w-9 h-9 rounded-xl bg-white/10 hover:bg-white/20 active:scale-95 text-slate-300 hover:text-white flex items-center justify-center transition-all ml-1"
                        aria-label="Close modal"
                    >
                        <X className="w-5 h-5" />
                    </button>
                </div>
            </div>

            {/* Main Embedded PDF Viewer */}
            <div className="w-full max-w-5xl flex-1 my-3 bg-slate-900/90 rounded-2xl overflow-hidden border border-white/10 shadow-2xl relative flex flex-col">
                <object
                    data={report.url}
                    type="application/pdf"
                    className="w-full h-full min-h-[400px] flex-1 rounded-2xl"
                >
                    <iframe
                        src={report.url}
                        title={report.name}
                        className="w-full h-full min-h-[400px] border-0"
                    >
                        <div className="p-8 text-center text-slate-300 flex flex-col items-center justify-center h-full gap-4">
                            <FileText className="w-12 h-12 text-orange-400 opacity-80" />
                            <p className="text-sm font-medium">{isTurkish ? 'Bu cihazda gömülü PDF önizlemesi desteklenmiyor.' : 'Embedded PDF preview is not supported on this device.'}</p>
                            <a
                                href={report.url}
                                target="_blank"
                                rel="noreferrer"
                                className="px-5 py-2.5 bg-orange-500 hover:bg-orange-600 text-white font-bold text-xs rounded-xl shadow-lg flex items-center gap-2"
                            >
                                <ExternalLink className="w-4 h-4" />
                                {isTurkish ? 'Tarayıcıda Aç' : 'Open in Browser'}
                            </a>
                        </div>
                    </iframe>
                </object>
            </div>

            {/* Footer Tip Notice */}
            <div className="text-[11px] text-slate-400 text-center font-medium">
                {isTurkish ? '💡 PDF belgesini çift parmakla yakınlaştırabilir veya yukarıdaki ' : '💡 Pinch to zoom the PDF, or save it using the '}
                <span className="text-orange-400 font-bold">{isTurkish ? 'İndir' : 'Download'}</span>
                {isTurkish ? ' butonuna basarak kaydedebilirsiniz.' : ' button above.'}
            </div>
        </div>
    );
};
